import ApiClient from '../shared/ApiCliente';
import { NewsItem, DashboardStats } from '../types';

type NewsApiItem = {
  analise: string;
  fato: string;
  impacto: string;
  dataProcessamento: string;
  noticia?: {
    url: string;
    titulo: string;
    data: string;
  };
  noticias?: Array<{
    url: string;
    titulo: string;
    data: string;
  }>;
  empresa: {
    id: string;
    nome: string;
    cnpj: string;
    tipo: string;
    tickerPrincipal?: string;
    tickers?: string[];
    aliases?: string[];
  } | null;
};

type AnalisePaginadaResponseDto = {
  analises: NewsApiItem[];
  totalSize: number;
  page: number;
  size: number;
};

export type PaginatedNewsResult = {
  items: NewsItem[];
  totalSize: number;
  page: number;
  size: number;
};

const parseImpactValue = (impact: string): NewsItem['impact'] => {
  const normalized = impact.toLowerCase();
  if (normalized.includes('positivo')) return 'positive';
  if (normalized.includes('negativo')) return 'negative';
  return 'neutral';
};

const mapApiNewsItem = (item: NewsApiItem): NewsItem => ({
  id:
    item.analise ||
    item.noticias?.[0]?.url ||
    item.noticia?.url ||
    `${item.empresa?.id || 'empresa'}-${item.dataProcessamento}`,
  analysisId: item.analise,
  url: item.noticias?.[0]?.url || item.noticia?.url,
  title:
    item.noticias?.[0]?.titulo ||
    item.noticia?.titulo ||
    'Análise de mercado',
  summary: item.analise,
  fact: item.fato,
  impact: parseImpactValue(item.impacto),
  companyId: item.empresa?.id,
  company: item.empresa?.nome || 'Empresa não informada',
  time: item.dataProcessamento || item.noticias?.[0]?.data || item.noticia?.data || '',
  isRead: false,
  analysisNews: (item.noticias?.length ? item.noticias : item.noticia ? [item.noticia] : []).map((news) => ({
    url: news.url,
    title: news.titulo,
    time: news.data,
  })),
});

type CorrelatedNewsApiItem = {
  manchete: NewsApiItem;
  relacionadas: Array<NewsApiItem & { score?: number }>;
};

export type CorrelatedNewsGroup = {
  headline: NewsItem;
  related: Array<NewsItem & { score?: number }>;
};

// Mock data para notícias
const mockNewsItems: NewsItem[] = [
  {
    id: '1',
    title: 'Petrobras anuncia novo plano de investimentos em energia renovável',
    summary: 'A empresa planeja investir R$ 15 bilhões em projetos de energia solar e eólica nos próximos 3 anos, fortalecendo sua posição no mercado de energia limpa.',
    impact: 'positive',
    company: 'Petrobras',
    time: '2 horas atrás',
    isRead: false
  },
  {
    id: '2',
    title: 'Vale reporta queda de 8% na produção de minério de ferro',
    summary: 'A redução na produção foi causada por problemas operacionais em uma das principais minas. A empresa espera normalizar a produção até o final do trimestre.',
    impact: 'negative',
    company: 'Vale',
    time: '4 horas atrás',
    isRead: false
  },
  {
    id: '3',
    title: 'Ambev lança nova linha de cervejas premium no mercado brasileiro',
    summary: 'A empresa expande seu portfólio com foco no segmento premium, esperando aumentar a margem de lucro em 12% no próximo ano.',
    impact: 'positive',
    company: 'Ambev',
    time: '6 horas atrás',
    isRead: true
  },
  {
    id: '4',
    title: 'Itaú Unibanco mantém taxa de juros estável para empréstimos',
    summary: 'O banco decidiu manter as taxas de juros inalteradas, refletindo a estabilidade do cenário econômico atual.',
    impact: 'neutral',
    company: 'Itaú',
    time: '8 horas atrás',
    isRead: true
  },
  {
    id: '5',
    title: 'Magazine Luiza anuncia expansão para 50 novas cidades',
    summary: 'A varejista planeja abrir lojas físicas e centros de distribuição em cidades do interior, aumentando sua presença nacional.',
    impact: 'positive',
    company: 'Magazine Luiza',
    time: '10 horas atrás',
    isRead: false
  },
  {
    id: '6',
    title: 'Bradesco enfrenta desafios com aumento de inadimplência',
    summary: 'O banco registrou aumento de 2.3% na inadimplência no último trimestre, impactando os resultados financeiros.',
    impact: 'negative',
    company: 'Bradesco',
    time: '12 horas atrás',
    isRead: true
  }
];

export const newsService = {
  getNews: async (params?: {
    empresa?: string;
    dataInicio?: string;
    dataFim?: string;
    page?: number;
    size?: number;
  }): Promise<NewsItem[]> => {
    try {
      const response = await ApiClient.get<NewsApiItem[] | AnalisePaginadaResponseDto>('/noticias', {
        params: params || {},
      });

      if (Array.isArray(response.data)) {
        return response.data.map(mapApiNewsItem);
      }

      return (response.data.analises || []).map(mapApiNewsItem);
    } catch (error) {
      console.error('Erro ao buscar notícias:', error);
      return [];
    }
  },
  getNewsPaginated: async (params?: {
    empresa?: string;
    dataInicio?: string;
    dataFim?: string;
    page?: number;
    size?: number;
  }): Promise<PaginatedNewsResult> => {
    try {
      const requestParams = {
        page: 0,
        size: 20,
        ...params,
      };
      const response = await ApiClient.get<NewsApiItem[] | AnalisePaginadaResponseDto>('/noticias', {
        params: requestParams,
      });

      if (Array.isArray(response.data)) {
        return {
          items: response.data.map(mapApiNewsItem),
          totalSize: response.data.length,
          page: requestParams.page,
          size: requestParams.size,
        };
      }

      return {
        items: (response.data.analises || []).map(mapApiNewsItem),
        totalSize: response.data.totalSize || 0,
        page: response.data.page ?? requestParams.page,
        size: response.data.size ?? requestParams.size,
      };
    } catch (error) {
      console.error('Erro ao buscar notícias paginadas:', error);
      return {
        items: [],
        totalSize: 0,
        page: params?.page || 0,
        size: params?.size || 20,
      };
    }
  },
  getCorrelatedNews: async (companyId: string): Promise<CorrelatedNewsGroup[]> => {
    try {
      const response = await ApiClient.get<CorrelatedNewsApiItem[]>(`/noticias/correlatas/${companyId}`);
      return response.data.map(group => ({
        headline: mapApiNewsItem(group.manchete),
        related: group.relacionadas.map(item => ({
          ...mapApiNewsItem(item),
          score: item.score,
        })),
      }));
    } catch (error) {
      console.error('Erro ao buscar notícias correlatas:', error);
      return [];
    }
  },
  searchNewsByContext: async (texto: string): Promise<NewsItem[]> => {
    try {
      const response = await ApiClient.post<NewsApiItem[]>('/noticias/busca-contexto', { texto });
      return response.data.map(mapApiNewsItem);
    } catch (error) {
      console.error('Erro ao buscar notícias por contexto:', error);
      return [];
    }
  },
  // Buscar todas as notícias
  getAllNews: async (): Promise<NewsItem[]> => {
    // Simular delay de API
    await new Promise(resolve => setTimeout(resolve, 500));
    return [...mockNewsItems];
  },

  // Buscar notícias por empresa
  getNewsByCompany: async (companyId: string): Promise<NewsItem[]> => {
    await new Promise(resolve => setTimeout(resolve, 300));
    return mockNewsItems.filter(news => 
      news.company.toLowerCase().includes(companyId.toLowerCase())
    );
  },

  // Buscar notícias não lidas
  getUnreadNews: async (): Promise<NewsItem[]> => {
    await new Promise(resolve => setTimeout(resolve, 200));
    return mockNewsItems.filter(news => !news.isRead);
  },

  // Marcar notícia como lida
  markAsRead: async (newsId: string): Promise<boolean> => {
    await new Promise(resolve => setTimeout(resolve, 200));
    const newsIndex = mockNewsItems.findIndex(news => news.id === newsId);
    if (newsIndex !== -1) {
      mockNewsItems[newsIndex].isRead = true;
      return true;
    }
    return false;
  },

  // Buscar estatísticas do dashboard
  getDashboardStats: async (): Promise<DashboardStats> => {
    await new Promise(resolve => setTimeout(resolve, 300));
    const totalNews = mockNewsItems.length;
    const positiveNews = mockNewsItems.filter(news => news.impact === 'positive').length;
    const negativeNews = mockNewsItems.filter(news => news.impact === 'negative').length;
    const neutralNews = mockNewsItems.filter(news => news.impact === 'neutral').length;
    const unreadCount = mockNewsItems.filter(news => !news.isRead).length;

    return {
      totalNews,
      positiveNews,
      negativeNews,
      neutralNews,
      unreadCount
    };
  },

  // Buscar notícias por impacto
  getNewsByImpact: async (impact: 'positive' | 'negative' | 'neutral'): Promise<NewsItem[]> => {
    await new Promise(resolve => setTimeout(resolve, 200));
    return mockNewsItems.filter(news => news.impact === impact);
  }
};

